import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

const Chip = ({ 
  children, 
  variant = 'default',
  size = 'medium',
  interactive = false,
  removable = false,
  disabled = false,
  onRemove = null,
  onClick = null,
  className = '',
  ...props
}) => {
  const variants = {
    default: 'bg-muted text-text border-border hover:bg-muted/80',
    accent: 'bg-accent/10 text-accent border-accent/20 hover:bg-accent/15',
    success: 'bg-success/10 text-success border-success/20 hover:bg-success/15',
    warning: 'bg-warning/10 text-warning border-warning/20 hover:bg-warning/15',
    danger: 'bg-danger/10 text-danger border-danger/20 hover:bg-danger/15',
    neutral: 'bg-surface text-text border-border hover:bg-bg',
    outline: 'bg-transparent text-text border-border hover:bg-muted/20',
  };

  const sizes = {
    small: 'px-2 py-1 text-scale-12 gap-1',
    medium: 'px-3 py-1.5 text-scale-14 gap-1.5',
    large: 'px-4 py-2 text-scale-16 gap-2',
  };

  const baseClasses = cn(
    'inline-flex items-center justify-center rounded-sm border transition-base font-medium',
    'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
    variants?.[variant],
    sizes?.[size],
    {
      'cursor-pointer hover:scale-[1.02]': interactive && !disabled,
      'cursor-default': !interactive && !removable,
      'opacity-50 cursor-not-allowed': disabled,
    },
    className
  );

  const handleClick = (e) => {
    if (disabled) return;
    onClick?.(e);
  };

  const handleRemove = (e) => {
    e?.stopPropagation();
    if (disabled) return;
    onRemove?.(e);
  };

  return (
    <span 
      className={baseClasses}
      onClick={interactive ? handleClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive && !disabled ? 0 : undefined}
      {...props}
    >
      {children}
      {removable && !disabled && (
        <button
          onClick={handleRemove}
          className="ml-1 hover:bg-black/10 rounded-full p-0.5 transition-fast focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="Remove"
          tabIndex={-1}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};

export default Chip;